import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { Users, Building2, Search, CheckCircle, Award } from 'lucide-react'

interface PlatformCandidateRecord {
  user_id: string
  full_name: string | null
  email: string
  tenant_count: number
  total_enrollments: number
  total_attempts: number
  total_passed: number
  total_certificates: number
  latest_activity_date: string | null
}

export function PlatformCandidatesPage() {
  useDocumentTitle('全受験者管理')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isPlatformAdmin } = useTenant()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<PlatformCandidateRecord[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (!isPlatformAdmin) {
      navigate('/mypage')
      return
    }

    if (user) {
      fetchPlatformCandidates()
    }
  }, [user, isPlatformAdmin])

  const fetchPlatformCandidates = async () => {
    try {
      setLoading(true)
      setError(null)

      // 全申込を取得
      const { data: enrollmentsData, error: enrollmentsError } = await supabase
        .from('enrollments')
        .select('user_id, tenant_id, created_at')

      if (enrollmentsError) throw enrollmentsError

      // ユニークなユーザーIDを取得
      const userIds = [...new Set(enrollmentsData?.map((e) => e.user_id) || [])]

      // 各ユーザーの情報を集約
      const candidateRecords: PlatformCandidateRecord[] = []

      for (const userId of userIds) {
        // ユーザー情報を取得
        const { data: userData } = await supabase
          .from('users')
          .select('full_name, email')
          .eq('user_id', userId)
          .single()

        const userEnrollments = enrollmentsData?.filter((e) => e.user_id === userId) || []

        // 関連テナント数
        const tenantCount = new Set(userEnrollments.map((e) => e.tenant_id)).size

        // 総申込数
        const totalEnrollments = userEnrollments.length

        // 総受験回数
        const { data: attemptsData } = await supabase
          .from('exam_attempts')
          .select('attempt_id, score, passing_score, submitted_at')
          .eq('user_id', userId)

        const totalAttempts = attemptsData?.length || 0
        const totalPassed = attemptsData?.filter((a) => a.score >= a.passing_score).length || 0

        // 総認定証数
        const { data: certificatesData } = await supabase
          .from('certificates')
          .select('certificate_id')
          .eq('user_id', userId)

        const totalCertificates = certificatesData?.length || 0

        // 最新活動日（最新受験日または最新申込日）
        const latestAttempt = attemptsData?.sort(
          (a, b) =>
            new Date(b.submitted_at || '').getTime() - new Date(a.submitted_at || '').getTime()
        )[0]

        const latestEnrollment = userEnrollments.sort(
          (a, b) =>
            new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
        )[0]

        const latestActivityDate =
          latestAttempt?.submitted_at ||
          latestEnrollment?.created_at ||
          null

        candidateRecords.push({
          user_id: userId,
          full_name: userData?.full_name || null,
          email: userData?.email || '',
          tenant_count: tenantCount,
          total_enrollments: totalEnrollments,
          total_attempts: totalAttempts,
          total_passed: totalPassed,
          total_certificates: totalCertificates,
          latest_activity_date: latestActivityDate,
        })
      }

      // 最新活動日でソート（新しい順）
      candidateRecords.sort((a, b) => {
        if (!a.latest_activity_date) return 1
        if (!b.latest_activity_date) return -1
        return (
          new Date(b.latest_activity_date).getTime() -
          new Date(a.latest_activity_date).getTime()
        )
      })

      setCandidates(candidateRecords)
    } catch (err: any) {
      console.error('Error fetching platform candidates:', err)
      setError(err.message || '受験者データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const filteredCandidates = candidates.filter(
    (c) =>
      c.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (loading) {
    return (
      <MainLayout>
        <Loading message="受験者データを読み込み中..." />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">受験者管理（全体）</h1>
          <p className="mt-2 text-gray-600">プラットフォーム全体の受験者情報を確認します</p>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} onRetry={fetchPlatformCandidates} />
          </div>
        )}

        {/* 検索バー */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="氏名またはメールアドレスで検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* 統計サマリー */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">総受験者数</p>
                <p className="text-2xl font-bold text-gray-900">{candidates.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">総申込数</p>
                <p className="text-2xl font-bold text-gray-900">
                  {candidates.reduce((sum, c) => sum + c.total_enrollments, 0)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">総合格数</p>
                <p className="text-2xl font-bold text-gray-900">
                  {candidates.reduce((sum, c) => sum + c.total_passed, 0)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <Award className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-sm text-gray-600">総認定証数</p>
                <p className="text-2xl font-bold text-gray-900">
                  {candidates.reduce((sum, c) => sum + c.total_certificates, 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 受験者一覧 */}
        {filteredCandidates.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? '該当する受験者がいません' : '受験者がいません'}
            </h3>
            <p className="text-gray-600">
              {searchQuery ? '検索条件を変更してください' : '受講申込があると表示されます'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    受験者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    関連テナント数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    総申込数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    総受験回数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    合格回数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    認定証
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    最終活動日
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCandidates.map((candidate) => (
                  <tr key={candidate.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {candidate.full_name || '未設定'}
                        </div>
                        <div className="text-sm text-gray-500">{candidate.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{candidate.tenant_count}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{candidate.total_enrollments}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{candidate.total_attempts}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {candidate.total_passed > 0 ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium text-green-600">
                              {candidate.total_passed}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">0</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {candidate.total_certificates > 0 ? (
                          <>
                            <Award className="h-4 w-4 text-orange-600" />
                            <span className="text-sm text-gray-900">
                              {candidate.total_certificates}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {candidate.latest_activity_date
                          ? new Date(candidate.latest_activity_date).toLocaleDateString('ja-JP')
                          : '-'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
